import { useMemo, useState, type FormEvent } from 'react';
import { Roles, type Role } from '@touristation/shared';
import { useAuth } from '../auth/AuthProvider';
import {
  useAudit, useCreateDepartment, useCreateEmployee, useCreateRole, useCreateShiftLocation,
  useDeleteDepartment, useDeleteRole, useDeleteShiftLocation,
  useDepartments, useEmployees, useInfostaff, useRoles, useShiftLocations, useTasks,
  useUpdateDepartment, useUpdateEmployee, useUpdateRole, useUpdateShiftLocation,
} from '../api/hooks';
import type { AppRole, Department, Employee, ShiftLocation } from '../api/types';
import { useT } from '../i18n/LangProvider';
import { Query, fmtDate, Pill } from '../ui/atoms';
import { DataTable } from '../ui/DataTable';
import { Icon } from '../ui/Icon';
import { useConfirm } from '../ui/confirm';
import { toast } from '../ui/toast';
import { OAuthClientsSection } from '../components/OAuthClientsSection';

const ALL_ROLES: { id: Role; label: string }[] = [
  { id: Roles.ADMIN, label: 'Admin' },
  { id: Roles.TEAM_LEAD, label: 'Team Lead' },
  { id: Roles.CUSTOMER_CARE, label: 'Customer Care' },
  { id: Roles.USER, label: 'Utente' },
];

const PERMISSIONS: { key: string; label: string }[] = [
  { key: 'admin', label: 'Accesso admin' },
  { key: 'infostaff_publish', label: 'Pubblica infostaff' },
  { key: 'tickets_all', label: 'Tutti i ticket' },
  { key: 'vault_manage', label: 'Gestione vault' },
  { key: 'users_manage', label: 'Gestione utenti' },
  { key: 'roles_manage', label: 'Gestione ruoli' },
];

const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `r${Date.now().toString().slice(-4)}`;

function Stat({ label, value, delta, accent }: { label: string; value: string | number; delta?: string; accent?: boolean }) {
  return (
    <div className="card stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={accent ? { color: 'var(--t-orange)' } : undefined}>{value}</div>
      {delta && <div className="stat__delta">{delta}</div>}
    </div>
  );
}

function UserRow({ e, depts }: { e: Employee; depts: Department[] }) {
  const tr = useT();
  const auth = useAuth();
  const confirm = useConfirm();
  const update = useUpdateEmployee();
  const isSelf = auth.sub === e.id;
  const toggleRole = (r: Role) => {
    const roles = e.roles.includes(r) ? e.roles.filter((x) => x !== r) : [...e.roles, r];
    update.mutate({ id: e.id, roles });
  };
  const toggleActive = async () => {
    // Guard: deactivating your own account would lock you out of the app.
    if (isSelf) return;
    if (e.active && !(await confirm({
      message: tr('Disattivare {name}? Non potrà più accedere.', { name: e.name }),
      confirmLabel: tr('Disattiva'), danger: true,
    }))) return;
    update.mutate({ id: e.id, active: !e.active });
  };
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="spaced" style={{ marginBottom: 8 }}>
        <div>
          <strong>{e.name}</strong>
          <div className="muted" style={{ fontSize: 12 }}>{e.email}{e.role ? ` · ${e.role}` : ''}</div>
        </div>
        <button className="btn btn--sm" onClick={toggleActive} disabled={isSelf}
                title={isSelf ? tr('Non puoi disattivare il tuo account') : (e.active ? tr('Disattiva account') : tr('Riattiva account'))}>
          {e.active ? tr('Attivo') : tr('Disattivato')}
        </button>
      </div>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {ALL_ROLES.map((r) => {
          const on = e.roles.includes(r.id);
          return (
            <button key={r.id} className={`task-filter ${on ? 'is-active' : ''}`} onClick={() => toggleRole(r.id)} style={{ fontSize: 12 }}>
              {on && <Icon name="check" size={11} />} {tr(r.label)}
            </button>
          );
        })}
      </div>
      <div className="row" style={{ gap: 6, alignItems: 'center' }}>
        <span className="muted" style={{ fontSize: 12 }}>{tr('Reparto:')}</span>
        <select className="input" style={{ padding: '4px 8px', fontSize: 12.5, height: 'auto' }} value={e.departmentId ?? ''} onChange={(ev) => update.mutate({ id: e.id, departmentId: ev.target.value || null })}>
          <option value="">{tr('— nessuno —')}</option>
          {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      {/* Contract: what the roster measures planned hours and leave against. */}
      <div className="row" style={{ gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <label className="row" style={{ gap: 5, alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 12 }}>{tr('Ore/sett.:')}</span>
          <input className="input" type="number" min={0} max={80} style={{ width: 74, padding: '4px 8px', fontSize: 12.5, height: 'auto' }}
                 defaultValue={e.weeklyHours ?? ''} placeholder="40"
                 onBlur={(ev) => {
                   const raw = ev.target.value.trim();
                   const next = raw === '' ? null : Math.max(0, Math.min(80, Number(raw) || 0));
                   if (next !== (e.weeklyHours ?? null)) update.mutate({ id: e.id, weeklyHours: next });
                 }} />
        </label>
        <label className="row" style={{ gap: 5, alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 12 }}>{tr('Ferie/anno (gg):')}</span>
          <input className="input" type="number" min={0} max={365} style={{ width: 74, padding: '4px 8px', fontSize: 12.5, height: 'auto' }}
                 defaultValue={e.leaveDaysYear ?? ''} placeholder="26"
                 onBlur={(ev) => {
                   const raw = ev.target.value.trim();
                   const next = raw === '' ? null : Math.max(0, Math.min(365, Number(raw) || 0));
                   if (next !== (e.leaveDaysYear ?? null)) update.mutate({ id: e.id, leaveDaysYear: next });
                 }} />
        </label>
      </div>
    </div>
  );
}

function RoleCard({ r }: { r: AppRole }) {
  const tr = useT();
  const confirm = useConfirm();
  const update = useUpdateRole();
  const del = useDeleteRole();
  const togglePerm = (key: string) => {
    const permissions = { ...r.permissions, [key]: !r.permissions[key] };
    update.mutate({ id: r.id, permissions });
  };
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="spaced" style={{ marginBottom: 8 }}>
        <Pill tone={r.color ?? 'neutral'}>{r.name}</Pill>
        <button className="btn btn--ghost btn--icon" onClick={async () => { if (await confirm({ message: tr('Eliminare il ruolo "{name}"?', { name: r.name }), confirmLabel: tr('Elimina'), danger: true })) del.mutate(r.id); }} title={tr('Elimina ruolo')}><Icon name="trash" size={13} /></button>
      </div>
      {r.description && <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}>{r.description}</p>}
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {PERMISSIONS.map((p) => {
          const on = !!r.permissions[p.key];
          return (
            <button key={p.key} className={`task-filter ${on ? 'is-active' : ''}`} onClick={() => togglePerm(p.key)} style={{ fontSize: 11.5 }}>
              {on && <Icon name="check" size={10} />} {tr(p.label)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DepartmentRow({ d, memberCount }: { d: Department; memberCount: number }) {
  const tr = useT();
  const confirm = useConfirm();
  const update = useUpdateDepartment();
  const del = useDeleteDepartment();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(d.name);
  const [short, setShort] = useState(d.short ?? '');
  return (
    <div className="card" style={{ padding: 14 }}>
      {editing ? (
        <div className="col" style={{ gap: 8 }}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={tr('Nome reparto')} />
          <div className="row" style={{ gap: 8 }}>
            <input className="input" value={short} onChange={(e) => setShort(e.target.value)} placeholder={tr('Sigla')} style={{ width: 100 }} />
            <button className="btn btn--sm btn--brand" onClick={() => { update.mutate({ id: d.id, name: name.trim() || d.name, short: short.trim() || undefined }); setEditing(false); }}>{tr('Salva')}</button>
            <button className="btn btn--sm" onClick={() => setEditing(false)}>{tr('Annulla')}</button>
          </div>
        </div>
      ) : (
        <div className="spaced">
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <span className={`pill pill--${d.color ?? 'neutral'}`} style={{ fontWeight: 700 }}>{d.short ?? '—'}</span>
            <div><strong>{d.name}</strong><div className="muted" style={{ fontSize: 12 }}>{tr('{n} membri', { n: memberCount })}</div></div>
          </div>
          <div className="cluster">
            <button className="btn btn--ghost btn--icon" onClick={() => setEditing(true)} title={tr('Modifica')}><Icon name="edit" size={13} /></button>
            <button className="btn btn--ghost btn--icon" onClick={async () => { if (await confirm({ message: tr('Eliminare il reparto "{name}"? I membri verranno scollegati.', { name: d.name }), confirmLabel: tr('Elimina'), danger: true })) del.mutate(d.id); }} title={tr('Elimina')}><Icon name="trash" size={13} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

const OFFICE_COLORS: Record<string, string> = {
  uff: '#e79aa6', vat: '#93cf8f', col: '#5bb8e6', nav: '#f2a63d', home: '#c3ace0', csam: '#9ad0c0', borgh: '#e6c34a',
};
const officeColor = (k: string) => OFFICE_COLORS[k] ?? '#c9c9c9';

function OfficeRow({ o }: { o: ShiftLocation }) {
  const tr = useT();
  const confirm = useConfirm();
  const update = useUpdateShiftLocation();
  const del = useDeleteShiftLocation();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(o.label);
  const [short, setShort] = useState(o.short ?? '');
  return (
    <div className="card" style={{ padding: 14 }}>
      {editing ? (
        <div className="col" style={{ gap: 8 }}>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={tr('Nome sede (es. Musei Vaticani)')} />
          <div className="row" style={{ gap: 8 }}>
            <input className="input" value={short} onChange={(e) => setShort(e.target.value)} placeholder={tr('Sigla')} style={{ width: 110 }} />
            <button className="btn btn--sm btn--brand" onClick={() => { update.mutate({ key: o.key, label: label.trim() || o.label, short: short.trim() || undefined }); setEditing(false); }}>{tr('Salva')}</button>
            <button className="btn btn--sm" onClick={() => setEditing(false)}>{tr('Annulla')}</button>
          </div>
        </div>
      ) : (
        <div className="spaced">
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <span style={{ background: officeColor(o.key), color: '#222', fontWeight: 700, fontSize: 12, borderRadius: 6, padding: '4px 9px' }}>{o.short ?? o.key.toUpperCase()}</span>
            <div><strong>{o.label}</strong><div className="muted mono" style={{ fontSize: 11.5 }}>{o.key}</div></div>
          </div>
          <div className="cluster">
            {/* Sedi arranged case by case (smart working) stay out of the generator. */}
            <button className={`task-filter ${o.autoAssign ? 'is-active' : ''}`} style={{ fontSize: 11.5 }}
                    onClick={() => update.mutate({ key: o.key, autoAssign: !o.autoAssign })}
                    title={o.autoAssign ? tr('Inclusa nella generazione automatica dei turni') : tr('Esclusa dalla generazione automatica dei turni')}>
              {o.autoAssign ? <><Icon name="check" size={10} /> {tr('Auto')}</> : tr('Solo manuale')}
            </button>
            <button className="btn btn--ghost btn--icon" onClick={() => setEditing(true)} title={tr('Modifica')}><Icon name="edit" size={13} /></button>
            <button className="btn btn--ghost btn--icon" onClick={async () => { if (await confirm({ message: tr('Eliminare la sede "{label}"?', { label: o.label }), confirmLabel: tr('Elimina'), danger: true })) del.mutate(o.key); }} title={tr('Elimina')}><Icon name="trash" size={13} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminScreen() {
  const tr = useT();
  const employees = useEmployees();
  const infostaff = useInfostaff();
  const tasks = useTasks();
  const roles = useRoles();
  const depts = useDepartments();
  const audit = useAudit();
  const offices = useShiftLocations();
  const createUser = useCreateEmployee();
  const createRole = useCreateRole();
  const createDept = useCreateDepartment();
  const createOffice = useCreateShiftLocation();

  const [userSearch, setUserSearch] = useState('');
  const [showNewUser, setShowNewUser] = useState(false);
  const [userForm, setUserForm] = useState<{ name: string; email: string; password: string; roles: Role[] }>({ name: '', email: '', password: '', roles: [Roles.USER] });
  const [showNewRole, setShowNewRole] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [showNewDept, setShowNewDept] = useState(false);
  const [deptName, setDeptName] = useState('');
  const [deptShort, setDeptShort] = useState('');
  const [showNewOffice, setShowNewOffice] = useState(false);
  const [officeLabel, setOfficeLabel] = useState('');
  const [officeShort, setOfficeShort] = useState('');

  const totalUsers = employees.data?.total ?? 0;
  const activeUsers = employees.data?.items.filter((e) => e.active).length ?? 0;
  const totalInfo = infostaff.data?.total ?? 0;
  const openTasks = tasks.data?.items.filter((t) => !t.done).length ?? 0;
  const roleCount = roles.data?.length ?? 0;
  const deptList = depts.data ?? [];

  const memberCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const e of employees.data?.items ?? []) if (e.departmentId) m[e.departmentId] = (m[e.departmentId] ?? 0) + 1;
    return m;
  }, [employees.data]);

  const filteredUsers = useMemo(() => {
    const items = employees.data?.items ?? [];
    const s = userSearch.toLowerCase().trim();
    return s ? items.filter((e) => e.name.toLowerCase().includes(s) || e.email.toLowerCase().includes(s)) : items;
  }, [employees.data, userSearch]);

  const toggleFormRole = (r: Role) => setUserForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));

  async function submitUser(e: FormEvent) {
    e.preventDefault();
    const pwd = userForm.password.trim();
    // Password is optional: empty = email the new hire an activation invite.
    if (!userForm.name.trim() || !userForm.email.trim() || (pwd.length > 0 && pwd.length < 8)) return;
    const email = userForm.email.trim();
    const created = await createUser.mutateAsync({
      name: userForm.name.trim(),
      email,
      roles: userForm.roles,
      ...(pwd ? { password: pwd } : {}),
    });
    if (pwd) {
      toast.success(tr('Utente creato.'));
    } else if (created?.inviteEmail?.sent) {
      toast.success(tr('Invito inviato a {email}.', { email }));
    } else {
      // User was created, but the activation email didn't go out (wrong address,
      // SMTP off, …). Warn instead of a misleading success.
      toast.error(tr('Utente creato, ma l’email di invito NON è stata recapitata a {email}. Controlla l’indirizzo o la configurazione SMTP.', { email }));
    }
    setUserForm({ name: '', email: '', password: '', roles: [Roles.USER] });
    setShowNewUser(false);
  }
  async function submitRole(e: FormEvent) {
    e.preventDefault();
    if (!roleName.trim()) return;
    await createRole.mutateAsync({ id: slugify(roleName), name: roleName.trim(), permissions: {} });
    setRoleName(''); setShowNewRole(false);
  }
  async function submitDept(e: FormEvent) {
    e.preventDefault();
    if (!deptName.trim()) return;
    await createDept.mutateAsync({ id: slugify(deptName).replace(/_/g, '-'), name: deptName.trim(), short: deptShort.trim() || undefined });
    setDeptName(''); setDeptShort(''); setShowNewDept(false);
  }
  async function submitOffice(e: FormEvent) {
    e.preventDefault();
    if (!officeLabel.trim()) return;
    await createOffice.mutateAsync({
      key: slugify(officeLabel).replace(/_/g, '-').slice(0, 40),
      label: officeLabel.trim(),
      short: (officeShort.trim() || officeLabel.trim().slice(0, 4)).toUpperCase(),
    });
    setOfficeLabel(''); setOfficeShort(''); setShowNewOffice(false);
  }

  return (
    <div className="content">
      <div className="page-head">
        <div><h1 className="h1">{tr('Dashboard Admin')}</h1><div className="page-head__sub">{tr('Utenti, ruoli, reparti e attività registrate.')}</div></div>
      </div>

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Stat label={tr('Utenti totali')} value={totalUsers} delta={tr('{n} attivi', { n: activeUsers })} />
        <Stat label={tr('Infostaff pubblicate')} value={totalInfo} delta={tr('comunicazioni interne')} />
        <Stat label={tr('Task aperti')} value={openTasks} accent />
        <Stat label={tr('Ruoli configurati')} value={roleCount} delta={tr('{n} reparti', { n: deptList.length })} />
      </div>

      {/* ── Users ── */}
      <section style={{ marginBottom: 26 }}>
        <div className="spaced" style={{ marginBottom: 10 }}>
          <h2 className="h2" style={{ fontSize: 15 }}>{tr('Gestione utenti')}</h2>
          <div className="cluster">
            <div className="row" style={{ gap: 6, alignItems: 'center', background: 'var(--bg-sunk,#f4f4f4)', borderRadius: 8, padding: '4px 10px' }}>
              <Icon name="search" size={13} />
              <input placeholder={tr('Cerca utente…')} value={userSearch} onChange={(e) => setUserSearch(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: 160 }} />
            </div>
            <button className="btn btn--brand btn--sm" onClick={() => setShowNewUser((s) => !s)}><Icon name="plus" size={13} /> {tr('Nuovo utente')}</button>
          </div>
        </div>

        {showNewUser && (
          <form className="card" style={{ padding: 18, marginBottom: 14 }} onSubmit={submitUser}>
            <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <input className="input" placeholder={tr('Nome e cognome')} value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required style={{ flex: 1, minWidth: 180 }} />
              <input className="input" type="email" placeholder={tr('Email aziendale')} value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required style={{ flex: 1, minWidth: 180 }} />
              <input className="input" type="password" placeholder={tr('Password (opzionale)')} value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} minLength={8} autoComplete="new-password" style={{ flex: 1, minWidth: 160 }} />
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              <Icon name="mail" size={12} /> {tr('Lascia la password vuota per inviare al dipendente un invito ad attivare l’account e scegliere la propria password.')}
            </div>
            <div className="row" style={{ gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>{tr('Ruoli:')}</span>
              {ALL_ROLES.map((r) => <button key={r.id} type="button" className={`task-filter ${userForm.roles.includes(r.id) ? 'is-active' : ''}`} onClick={() => toggleFormRole(r.id)} style={{ fontSize: 12 }}>{tr(r.label)}</button>)}
            </div>
            <div className="cluster" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn--sm" onClick={() => setShowNewUser(false)}>{tr('Annulla')}</button>
              <button type="submit" className="btn btn--sm btn--brand" disabled={createUser.isPending}>{userForm.password.trim() ? tr('Crea utente') : tr('Invia invito')}</button>
            </div>
          </form>
        )}

        <Query query={employees} empty={{ test: (d) => d.items.length === 0, title: tr('Nessun utente') }}>
          {() => (
            <>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>{tr('{n} utenti', { n: filteredUsers.length })}</div>
              <div className="grid grid-3" style={{ gap: 12 }}>
                {filteredUsers.map((e) => <UserRow key={e.id} e={e} depts={deptList} />)}
              </div>
            </>
          )}
        </Query>
      </section>

      {/* ── Roles ── */}
      <section style={{ marginBottom: 26 }}>
        <div className="spaced" style={{ marginBottom: 10 }}>
          <h2 className="h2" style={{ fontSize: 15 }}>{tr('Ruoli & permessi')}</h2>
          <button className="btn btn--brand btn--sm" onClick={() => setShowNewRole((s) => !s)}><Icon name="plus" size={13} /> {tr('Nuovo ruolo')}</button>
        </div>
        {showNewRole && (
          <form className="card" style={{ padding: 14, marginBottom: 12 }} onSubmit={submitRole}>
            <div className="row" style={{ gap: 8 }}>
              <input className="input" placeholder={tr('Nome del ruolo (es. Supervisor)')} value={roleName} onChange={(e) => setRoleName(e.target.value)} required style={{ flex: 1 }} />
              <button type="submit" className="btn btn--sm btn--brand" disabled={createRole.isPending}>{tr('Crea')}</button>
              <button type="button" className="btn btn--sm" onClick={() => setShowNewRole(false)}>{tr('Annulla')}</button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{tr('Dopo la creazione, attiva i permessi cliccando sulle etichette nella scheda del ruolo.')}</div>
          </form>
        )}
        <Query query={roles} empty={{ test: (d) => d.length === 0, title: tr('Nessun ruolo') }}>
          {(rs) => <div className="grid grid-3" style={{ gap: 12 }}>{rs.map((r) => <RoleCard key={r.id} r={r} />)}</div>}
        </Query>
      </section>

      {/* ── Departments ── */}
      <section style={{ marginBottom: 26 }}>
        <div className="spaced" style={{ marginBottom: 10 }}>
          <h2 className="h2" style={{ fontSize: 15 }}>{tr('Reparti')}</h2>
          <button className="btn btn--brand btn--sm" onClick={() => setShowNewDept((s) => !s)}><Icon name="plus" size={13} /> {tr('Nuovo reparto')}</button>
        </div>
        {showNewDept && (
          <form className="card" style={{ padding: 14, marginBottom: 12 }} onSubmit={submitDept}>
            <div className="row" style={{ gap: 8 }}>
              <input className="input" placeholder={tr('Nome reparto')} value={deptName} onChange={(e) => setDeptName(e.target.value)} required style={{ flex: 1 }} />
              <input className="input" placeholder={tr('Sigla')} value={deptShort} onChange={(e) => setDeptShort(e.target.value)} style={{ width: 100 }} />
              <button type="submit" className="btn btn--sm btn--brand" disabled={createDept.isPending}>{tr('Crea')}</button>
              <button type="button" className="btn btn--sm" onClick={() => setShowNewDept(false)}>{tr('Annulla')}</button>
            </div>
          </form>
        )}
        <Query query={depts} empty={{ test: (d) => d.length === 0, title: tr('Nessun reparto') }}>
          {(ds) => <div className="grid grid-2" style={{ gap: 12 }}>{ds.map((d) => <DepartmentRow key={d.id} d={d} memberCount={memberCounts[d.id] ?? 0} />)}</div>}
        </Query>
      </section>

      {/* ── Offices / work locations ── */}
      <section style={{ marginBottom: 26 }}>
        <div className="spaced" style={{ marginBottom: 10 }}>
          <div>
            <h2 className="h2" style={{ fontSize: 15 }}>{tr('Uffici e sedi')}</h2>
            <div className="muted" style={{ fontSize: 12 }}>{tr('Usati nei Turni e per raggruppare i todo per sede.')}</div>
          </div>
          <button className="btn btn--brand btn--sm" onClick={() => setShowNewOffice((s) => !s)}><Icon name="plus" size={13} /> {tr('Nuova sede')}</button>
        </div>
        {showNewOffice && (
          <form className="card" style={{ padding: 14, marginBottom: 12 }} onSubmit={submitOffice}>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <input className="input" placeholder={tr('Nome sede (es. Musei Vaticani)')} value={officeLabel} onChange={(e) => setOfficeLabel(e.target.value)} required style={{ flex: 1, minWidth: 200 }} />
              <input className="input" placeholder={tr('Sigla (es. VAT)')} value={officeShort} onChange={(e) => setOfficeShort(e.target.value)} style={{ width: 130 }} />
              <button type="submit" className="btn btn--sm btn--brand" disabled={createOffice.isPending}>{tr('Crea')}</button>
              <button type="button" className="btn btn--sm" onClick={() => setShowNewOffice(false)}>{tr('Annulla')}</button>
            </div>
          </form>
        )}
        <Query query={offices} empty={{ test: (d) => d.length === 0, title: tr('Nessuna sede'), hint: tr('Aggiungi ufficio, point of sale o smart working.') }}>
          {(os) => <div className="grid grid-2" style={{ gap: 12 }}>{os.map((o) => <OfficeRow key={o.key} o={o} />)}</div>}
        </Query>
      </section>

      {/* ── SSO / OAuth client apps ── */}
      <OAuthClientsSection />

      {/* ── Audit ── */}
      <section>
        <h2 className="h2" style={{ fontSize: 15, marginBottom: 10 }}>{tr('Audit log')}</h2>
        <Query query={audit} empty={{ test: (d) => d.items.length === 0, title: tr('Nessun evento registrato') }}>
          {(d) => (
            <DataTable
              rows={d.items}
              rowKey={(r) => r.id}
              columns={[
                { header: tr('Quando'), render: (r) => fmtDate(r.createdAt) },
                { header: tr('Attore'), render: (r) => r.actorEmail ?? '—' },
                { header: tr('Azione'), render: (r) => <code style={{ fontFamily: 'var(--font-mono)' }}>{r.action}</code> },
                { header: tr('Risorsa'), render: (r) => `${r.resource}${r.resourceId ? ` · ${r.resourceId.slice(0, 8)}` : ''}` },
              ]}
            />
          )}
        </Query>
      </section>
    </div>
  );
}
